import { TradesWebSocketManager } from '../library';
import { useWebSocket } from './use-websocket';
import { useCallback, useEffect } from 'react';
import { TradeData, TradeEventTypes, TradesCallback } from '../types';
import { useSocketManagerContext } from '../providers/context';
import { WS_TRACKING_EVENTS } from '../constants/nr-events';

export interface IUseTradeEventsOptions {
  socketPath: string;
  onMessage?: (data: TradeData) => void;
  type: TradeEventTypes;
}

export function useTradeEvents({
  socketPath,
  onMessage,
  type,
}: IUseTradeEventsOptions) {
  const { tradesConnection } = useWebSocket();
  const context = useSocketManagerContext();
  const trackEvent = context.config.analytics?.trackEvent;

  const handleMessage: TradesCallback = useCallback(
    (message) => {
      if (!message) return;
      onMessage?.(message);
    },
    [onMessage],
  );

  useEffect(() => {
    let sub;

    if (socketPath) {
      try {
        if (
          tradesConnection &&
          tradesConnection.readyState === WebSocket.OPEN
        ) {
          if (type === TradeEventTypes.ORDER) {
            sub = TradesWebSocketManager.getTradesInstance().subscribeOrders(
              socketPath,
              handleMessage,
            );
          } else if (type === TradeEventTypes.POSITION) {
            sub = TradesWebSocketManager.getTradesInstance().subscribePositions(
              socketPath,
              handleMessage,
            );
          } else if (type === TradeEventTypes.POSITION_REFRESH) {
            sub =
              TradesWebSocketManager.getTradesInstance().subscribePositionRefresh(
                socketPath,
                handleMessage,
              );
          }
        } else {
          console.error('Trades connection not ready');
          trackEvent?.(WS_TRACKING_EVENTS.HOOK_CONNECTION_NOT_READY, {
            hook: 'useTradeEvents',
            socketPath,
            type,
            readyState: tradesConnection?.readyState ?? 'unknown',
          });
        }
      } catch (error: unknown) {
        console.error('Error subscribing to trades', error);
        trackEvent?.(WS_TRACKING_EVENTS.HOOK_SUBSCRIBE_ERROR, {
          hook: 'useTradeEvents',
          socketPath,
          type,
          readyState: tradesConnection?.readyState ?? 'unknown',
          error: error instanceof Error ? error.message : String(error),
        });
      }
    } else {
      trackEvent?.(WS_TRACKING_EVENTS.HOOK_SOCKET_PATH_NOT_FOUND, {
        hook: 'useTradeEvents',
        socketPath,
        type,
        readyState: tradesConnection?.readyState ?? 'unknown',
      });
    }

    return () => sub?.();
  }, [tradesConnection, socketPath, type, handleMessage, trackEvent]);
}
